import React from 'react';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix, getBrandVatRate } from '../../../../shared/utils/brandCurrency';
import BrandProtectedImg from '../../../../shared/components/custom/BrandProtectedImg';
import { resolveCompanyLogoSrc } from '../../../../shared/utils/companyLogo';
import { getDocumentPreviewTitle, getDocumentTypePhrase, buildAmountClosingLine, numberToBrandWords, PREVIEW_LABELS, PREVIEW_FIELD_LABELS } from '../../../../shared/i18n/documentPreviewLabels';
import { SAUDI_BRAND } from '../../../../products/saudi/config/saudiBrandConfig';
import { parseFrenchDzAmountDisplay, resolveShippingSlipDeclaredValueAmount } from '../../../../shared/utils/currency';
import {
  RECEPTION_INDICATIVE_HT_LABEL,
  isReceptionNoteDocumentType,
  previewItemHasLineContent,
  sumReceptionIndicativeHT,
  resolveCreditNotePdfTitles,
} from '../../../../shared/utils/documentTypeUtils';

const HeritageInvoicePreview = ({ documentType, formData = {}, clientData = {}, partnerType, paperSize = 'A4', settings = {}, totalsRecapRows = null, documentPartnerLabel, documentDetailsLabel, warehouseOriginData, warehouseDestinationData, exitNoteData, headerDetailRows, documentDetailRows, documentInfoSections = [], supplementaryInfo = { show: false, label: '', text: '' }, bonPourInfo = { enabled: false, title: '', name: '', role: '', date: '', place: '' }, etablieParInfo = { enabled: false, name: '', role: '', date: '', place: '' }, recueParInfo = { enabled: false, name: '', role: '', date: '', place: '' }, fillPageAboveFooter = false, invoiceRenderPart = 'all', continuationHint = false, itemsTableTotalRows: itemsTableTotalRowsProp = null, signatureBlockPinToFooter = true, compactNotesSignatureSpacing = false, dataListExportContent = null, listExportTitle = null, listExportContinuation = false, listExportPageIndex = 0, listExportPageCount = 1 }) => {
  const isA5 = String(paperSize || 'A4').toUpperCase() === 'A5';
  const A5_SCALE = 0.707106781;
  const fs = (px, minPx = 7) => `${isA5 ? Math.max(minPx, Math.round(Number(px) * A5_SCALE)) : Number(px)}px`;
  const sp = (px, minPx = 4) => (isA5 ? Math.max(minPx, Math.round(Number(px) * A5_SCALE)) : Number(px));
  const creditNotePdf = resolveCreditNotePdfTitles(formData, documentType, partnerType);

  const getDocumentTitle = (type) => getDocumentPreviewTitle(type, creditNotePdf);

  // Check document types first (needed for billToTitle)
  const isPaymentVoucher = documentType === 'payment-voucher' || documentType === 'payment_voucher' || documentType === 'payment_slip';
  const isPaymentReceipt = documentType === 'payment-receipt' || documentType === 'payment_receipt';
  const isRequest = documentType === 'quote-request' || documentType === 'proforma-request';
  const isShippingSlip = documentType === 'shipping-slip' || documentType === 'shipping';
  const isTransferNote = documentType === 'transfer-note' || documentType === 'transfer_note';
  const isReceptionNote = isReceptionNoteDocumentType(documentType);
  const showPrices = !isRequest && !isShippingSlip;

  // Partner type label (Client or Fournisseur)
  const partnerLabel = partnerType === 'supplier' ? PREVIEW_LABELS.supplier : PREVIEW_LABELS.client;
  const billToTitle = documentPartnerLabel || (partnerType === 'supplier' ? PREVIEW_LABELS.supplier : PREVIEW_LABELS.addressee);
  const useHeaderRows = Array.isArray(headerDetailRows) && headerDetailRows.length >= 3;
  const useDetailRows = Array.isArray(documentDetailRows) && documentDetailRows.length > 0;
  const sup = supplementaryInfo || {};
  const bon = bonPourInfo || {};
  const supHeading = sup.show && sup.label ? sup.label : PREVIEW_LABELS.notesAndConditions;
  const supBody = sup.show ? String(sup.text || '').trim() : '';
  const showSupplementary = sup.show;
  
  // Extract data with fallbacks
  const company = settings?.company || {};
  // Filter out empty/placeholder items (items with no product_name, no description, or that are essentially empty)
  const items = (formData?.items || []).filter(previewItemHasLineContent);
  const paymentLines = formData?.paymentLines || [];
  const isBody = invoiceRenderPart === 'body';
  const isClosing = invoiceRenderPart === 'closing';
  const isDataListExport = dataListExportContent != null;
  const useClosingLayoutFlex = fillPageAboveFooter && !isBody;
  const centerSignaturesInFooterGap =
    useClosingLayoutFlex && !isClosing && signatureBlockPinToFooter && !(isPaymentVoucher || isPaymentReceipt);
  const itemCount = items.length;
  const minTotalRows = itemCount === 0 ? 1 : itemCount;
  
  // When in body mode (splitting) with products, use the provided row count to fill page with empty rows
  // Otherwise, use the max of 9 or itemCount
  let effectiveTableRows;
  if (isBody && itemsTableTotalRowsProp != null && itemCount > 0) {
    // In splitting mode on body page with products: use the prop directly to fill the page
    effectiveTableRows = Math.max(itemsTableTotalRowsProp, itemCount);
  } else {
    const budgetFromProp = itemsTableTotalRowsProp != null ? itemsTableTotalRowsProp : Math.max(9, itemCount);
    effectiveTableRows = Math.max(minTotalRows, budgetFromProp, itemCount);
  }

  // For payment vouchers/receipts: calculate effective payment rows
  const paymentLineCount = paymentLines.length;
  const minPaymentRows = paymentLineCount === 0 ? 1 : paymentLineCount;
  let effectivePaymentRows;
  if (isPaymentVoucher || isPaymentReceipt) {
    if (isBody && itemsTableTotalRowsProp != null && paymentLineCount > 0) {
      // In body-closing split mode: use the large row count from wrapper to fill first page
      effectivePaymentRows = Math.max(itemsTableTotalRowsProp, paymentLineCount);
    } else {
      // In single-page mode: max 5 empty rows (like before)
      const maxSinglePageRows = Math.max(5, paymentLineCount);
      effectivePaymentRows = maxSinglePageRows;
    }
  } else {
    effectivePaymentRows = paymentLineCount;
  }
  const documentNumber = formData?.number || 'N/A';
  const documentDate = formData?.date || new Date().toISOString().split('T')[0];
  const dueDate = formData?.dueDate || documentDate;
  const paymentMethod = formData?.paymentMethod || 'تحويل بنكي';
  
  // Client/Partner data
  const isExitNote = formData?.exitNotePartnerType != null || (documentType === 'exit-note');
  const exitNotePType = exitNoteData?.partnerType || formData?.exitNotePartnerType || 'client';
  const isExitNoteOther = isExitNote && exitNotePType === 'other';
  const isExitNoteWarehouse = isExitNote && exitNotePType === 'warehouse';
  const partnerName = isExitNoteOther
    ? (exitNoteData?.beneficiary || exitNoteData?.motif || '---')
    : isExitNoteWarehouse
      ? (exitNoteData?.warehouseName || '---')
      : (clientData?.name || '---');
  const partnerAddress = isExitNoteOther
    ? ''
    : isExitNoteWarehouse
      ? (exitNoteData?.warehouseAddress || '')
      : (clientData?.address || '');
  const partnerNif = clientData?.nif || '';
  const partnerRc = clientData?.rc || '';
  const partnerNis = clientData?.nis || '';
  const partnerArt = clientData?.article || '';
  const partnerPhone = clientData?.phone || '';
  const partnerEmail = clientData?.email || '';
  const partnerContact = clientData?.contact || '';
  
  // Company data
  const companyName = (company?.name || SAUDI_BRAND.appName).toUpperCase();
  const companyNameArabic = company?.nameArabic || '';
  const companyActivity = company?.activity || '';
  const companyServices = company?.services || '';
  const companyAddress = company?.address || '';
  const companyPhone = company?.phone || '';
  const companyEmail = company?.email || '';
  const companyWebsite = company?.website || '';
  const companyNif = company?.nif || '';
  // Get active RC (السجل التجاري1, السجل التجاري2, or السجل التجاري3)
  const activeRC = company?.activeRC || 'rc1';
  const companyRc = activeRC === 'rc3' ? (company?.rc3 || '') 
                  : activeRC === 'rc2' ? (company?.rc2 || '') 
                  : (company?.rc || '');
  const companyNis = company?.nis || '';
  const companyArt = company?.articleNumber || '';
  
  // Bank details
  const bankName = company?.bankName || '';
  const bankAccount = company?.bankAccount || '';
  const bankRib = company?.rib || '';
  
  // Additional form data
  const validity = formData?.validity || '';
  const discount = formData?.discount || 0;
  const shipping = formData?.shipping || 0;
  const reference = formData?.reference || '';
  
  // Calculate totals from items
  const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
  const taxRate = formData?.taxRate || 19; // TVA from formData or default
  const taxAmount = subtotal * (taxRate / 100);
  const total = subtotal + taxAmount - discount + shipping;

  const getFinalAmount = () => {
    if (isReceptionNote) return sumReceptionIndicativeHT(formData);
    if (isShippingSlip) return resolveShippingSlipDeclaredValueAmount(formData);
    if (isPaymentVoucher || isPaymentReceipt) {
      return paymentLines.reduce((sum, line) => sum + (parseFloat(line.amount) || 0), 0);
    }
    if (totalsRecapRows && totalsRecapRows.length > 0) {
      const finalRow = totalsRecapRows.find((row) => row.isFinal);
      if (finalRow && finalRow.text) {
        const parsed = parseFrenchDzAmountDisplay(finalRow.text);
        if (Number.isFinite(parsed)) return parsed;
      }
    }
    return total;
  };
  const finalAmount = getFinalAmount(); const finalAmountInWords = numberToBrandWords(finalAmount);
  const getDocumentTypeFr = (type) => getDocumentTypePhrase(type, creditNotePdf);
  const documentTypeFr = getDocumentTypeFr(documentType);

  const companyLogoSrc = resolveCompanyLogoSrc(settings?.company?.logo);

  return (
    <div className="heritage-invoice-preview" style={{ 
      fontFamily: '"Playfair Display", serif', 
      direction: isDataListExport ? 'rtl' : 'ltr', 
      backgroundColor: 'transparent', 
      padding: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      width: '100%',
      height: '100%',
      ...(fillPageAboveFooter ? { flex: 1, minHeight: 0 } : {}) 
    }}>
      <div className="heritage-invoice-container" style={{
        background: 'transparent',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        margin: '0 auto',
        fontSize: isA5 ? '10.5px' : '12px',
        boxShadow: 'none',
        position: 'relative',
        border: 'none',
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        ...(fillPageAboveFooter ? { flex: 1, minHeight: 0 } : {}),
      }}>
        {/* Decorative frame is drawn in TemplateWrapper around document + footer so المقر stays inside the outline */}

        {/* Header */}
        {isDataListExport && listExportContinuation ? (
          <div style={{
            padding: '10px 32px 8px',
            borderBottom: '1px solid #e7e3da',
            textAlign: 'right',
          }}>
            <div style={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              fontSize: fs(16),
              color: '#3a3025',
              marginBottom: '2px',
            }}>
              {listExportTitle || getDocumentTitle(documentType)}
            </div>
            {listExportPageCount > 1 && (
              <div style={{
                fontSize: fs(11),
                color: '#7d735f',
                fontFamily: '"Cormorant Garamond", serif',
              }}>
                صفحة {listExportPageIndex + 1} من {listExportPageCount}
              </div>
            )}
          </div>
        ) : (
        <div style={{
          padding: '16px 32px 5px',
          borderBottom: '1px solid #e7e3da',
          position: 'relative',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}>
          {/* Logo & Company — first in DOM → right side in RTL export */}
          <div style={{ flex: '1', maxWidth: '70%', alignSelf: 'flex-start', textAlign: isDataListExport ? 'right' : 'left' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '5px',
            }}>
              <BrandProtectedImg 
                src={companyLogoSrc} 
                alt={companyName || PREVIEW_LABELS.companyLogoAlt} 
                style={{
                  width: fs(45, 4),
                  height: fs(45, 4),
                  marginInlineEnd: fs(12, 4),
                  objectFit: 'contain'
                }}
              />
              <div style={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: '700', 
                fontSize: fs(24), 
                color: '#3a3025', 
                letterSpacing: '1px',
                borderBottom: '2px solid #c7b299',
                paddingBottom: '5px'
              }}>{companyName}</div>
            </div>
            {/* Company info */}
            <div style={{ fontFamily: '"Playfair Display", serif', fontSize: fs(11), color: '#7d735f', lineHeight: '1.7', marginBottom: '0px', maxWidth: '100%' }}>
              {companyNameArabic && <div style={{ direction: 'rtl', textAlign: 'right', marginBottom: '2px', fontSize: fs(12), fontFamily: '"Noto Sans Arabic", "Cormorant Garamond", "Segoe UI Historic", serif' }}>{companyNameArabic}</div>}
              {companyActivity && <div style={{ color: '#3a3025', marginBottom: '1px', fontFamily: '"Cormorant Garamond", serif', fontSize: fs(12), fontWeight: '600' }}>{companyActivity}</div>}
              <div style={{ display: 'inline-block' }}>
                {companyServices && <div style={{ fontSize: fs(10), fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic', marginBottom: '0px' }}>{companyServices}</div>}
                <div style={{ borderBottom: '1px solid #e7e3da', marginTop: '4px', marginBottom: '4px' }}></div>
                <div style={{ fontSize: fs(9, 6), fontFamily: '"Cormorant Garamond", serif', color: '#7d735f', lineHeight: '1.4' }}>
                  {[
                    companyNif && <span key="nif"><strong>{PREVIEW_FIELD_LABELS.vatNumber}</strong> {companyNif}</span>,
                    companyRc && <span key="rc"><strong>{PREVIEW_FIELD_LABELS.commercialRegister}</strong> {companyRc}</span>,
                    companyNis && <span key="nis"><strong>{PREVIEW_FIELD_LABELS.unifiedNationalNumber}</strong> {companyNis}</span>,
                    companyArt && <span key="art"><strong>{PREVIEW_FIELD_LABELS.taxArticleNumber}</strong> {companyArt}</span>
                  ].filter(Boolean).reduce((acc, curr, idx, arr) => 
                    idx === 0 ? [curr] : [...acc, ' / ', curr], []
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Document Title */}
          <div style={{
            textAlign: 'right',
            alignSelf: 'flex-start'
          }}>
            <div style={{ 
              fontFamily: '"Playfair Display", serif',
              fontWeight: '700', 
              fontSize: fs(28), 
              color: '#3a3025',
              marginBottom: '3px',
              letterSpacing: '2px'
            }}>{listExportTitle || getDocumentTitle(documentType)}</div>
            <div style={{
              fontSize: fs(14),
              color: '#7d735f',
              marginBottom: '3px',
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: 'italic'
            }}>
              {PREVIEW_FIELD_LABELS.reference} <span style={{ color: '#3a3025', fontWeight: '600' }}>{documentNumber}</span>
            </div>
            <div style={{
              fontSize: fs(12),
              color: '#7d735f',
              fontFamily: '"Cormorant Garamond", serif',
              lineHeight: '1.7'
            }}>
              {isDataListExport ? (
                <>
                  {PREVIEW_LABELS.issueDate}: <span style={{ color: '#3a3025', fontWeight: '600' }}>{new Date(documentDate).toLocaleDateString('ar-SA', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </>
              ) : useHeaderRows ? (
                <>
                  {headerDetailRows[1].label} <span style={{ color: '#3a3025', fontWeight: '600' }}>{headerDetailRows[1].value}</span><br />
                  {headerDetailRows[2].label} <span style={{ color: '#3a3025', fontWeight: '600' }}>{headerDetailRows[2].value}</span>
                </>
              ) : (
                <>
                  {PREVIEW_LABELS.issuedOnPrefix} <span style={{ color: '#3a3025', fontWeight: '600' }}>{new Date(documentDate).toLocaleDateString('ar-SA', { day: 'numeric', month: 'long', year: 'numeric' })}</span><br />
                  {PREVIEW_LABELS.dueOnPrefix} <span style={{ color: '#3a3025', fontWeight: '600' }}>{new Date(dueDate).toLocaleDateString('ar-SA', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </>
              )}
            </div>
          </div>
        </div>
        )}

        {!isDataListExport && !isClosing && (
        <div style={{
          padding: '15px 40px 15px 40px',
          borderBottom: '1px solid #e7e3da',
          backgroundColor: '#fbfaf8'
        }}>
          <div style={{
            display: isShippingSlip ? 'grid' : 'flex',
            gridTemplateColumns: isShippingSlip ? '1fr 1fr 1fr' : undefined,
            justifyContent: isShippingSlip ? undefined : 'space-between',
            flexWrap: isShippingSlip ? undefined : 'wrap',
            gap: isShippingSlip ? '15px' : '30px'
          }}>
            {isShippingSlip ? (
              <>
                <div>
                  <div style={{
                    fontSize: fs(12),
                    fontWeight: '700',
                    color: '#3a3025',
                    marginBottom: '10px',
                    fontFamily: '"Playfair Display", serif',
                    position: 'relative',
                    paddingBottom: '8px',
                    display: 'inline-block'
                  }}>
                    {billToTitle}
                    <div style={{
                      position: 'absolute',
                      bottom: '0',
                      left: '0',
                      width: '40px',
                      height: '1px',
                      backgroundColor: '#c7b299'
                    }}></div>
                  </div>
                  {((partnerName && partnerName !== '---') || partnerAddress || partnerNif || partnerRc || partnerArt || partnerNis || partnerPhone || partnerEmail) ? (
                  <>
                  <div style={{ fontWeight: '600', fontSize: fs(16), marginBottom: '10px', color: '#3a3025', fontFamily: '"Playfair Display", serif' }}>
                    {partnerName}
                  </div>
                  <table style={{ borderCollapse: 'collapse', fontSize: fs(12), fontFamily: '"Cormorant Garamond", serif' }}>
                    <tbody>
                      {partnerAddress && (
                        <tr>
                          <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{PREVIEW_FIELD_LABELS.address}</td>
                          <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{partnerAddress}</td>
                        </tr>
                      )}
                      {partnerNif && (
                        <tr>
                          <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{PREVIEW_FIELD_LABELS.vatNumber}</td>
                          <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{partnerNif}</td>
                        </tr>
                      )}
                      {partnerRc && (
                        <tr>
                          <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{PREVIEW_FIELD_LABELS.commercialRegister}</td>
                          <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{partnerRc}</td>
                        </tr>
                      )}
                      {partnerArt && (
                        <tr>
                          <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{PREVIEW_FIELD_LABELS.taxArticleNumber}</td>
                          <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{partnerArt}</td>
                        </tr>
                      )}
                      {partnerNis && (
                        <tr>
                          <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{PREVIEW_FIELD_LABELS.unifiedNationalNumber}</td>
                          <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{partnerNis}</td>
                        </tr>
                      )}
                      {partnerPhone && (
                        <tr>
                          <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{PREVIEW_FIELD_LABELS.phone}</td>
                          <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{partnerPhone}</td>
                        </tr>
                      )}
                      {partnerEmail && (
                        <tr>
                          <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{PREVIEW_FIELD_LABELS.email}</td>
                          <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{partnerEmail}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  </>
                  ) : (
                    <div style={{ fontWeight: '600', fontSize: fs(16), color: '#3a3025', fontFamily: '"Playfair Display", serif' }}>---</div>
                  )}
                </div>
                <div>
                  <div style={{
                    fontSize: fs(12),
                    fontWeight: '700',
                    color: '#3a3025',
                    marginBottom: '10px',
                    fontFamily: '"Playfair Display", serif',
                    position: 'relative',
                    paddingBottom: '8px',
                    display: 'inline-block'
                  }}>
                    تفاصيل المستند
                    <div style={{
                      position: 'absolute',
                      bottom: '0',
                      left: '0',
                      width: '40px',
                      height: '1px',
                      backgroundColor: '#c7b299'
                    }}></div>
                  </div>
                  {useDetailRows && documentDetailRows.length > 0 ? (
                    <table style={{ borderCollapse: 'collapse', fontSize: fs(12), fontFamily: '"Cormorant Garamond", serif' }}>
                      <tbody>
                        {documentDetailRows.map((row) => (
                          <tr key={row.id}>
                            <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{row.label}:</td>
                            <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{row.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ fontWeight: '600', fontSize: fs(16), color: '#3a3025', fontFamily: '"Playfair Display", serif' }}>---</div>
                  )}
                </div>
                <div>
                  <div style={{
                    fontSize: fs(12),
                    fontWeight: '700',
                    color: '#3a3025',
                    marginBottom: '10px',
                    fontFamily: '"Playfair Display", serif',
                    position: 'relative',
                    paddingBottom: '8px',
                    display: 'inline-block'
                  }}>
                  {PREVIEW_LABELS.packageDetails}
                    <div style={{
                      position: 'absolute',
                      bottom: '0',
                      left: '0',
                      width: '40px',
                      height: '1px',
                      backgroundColor: '#c7b299'
                    }}></div>
                  </div>
                  {(formData.shipmentPackageType || formData.shipmentProductType || (formData.shipmentPackageCount != null && formData.shipmentPackageCount !== '' && Number(formData.shipmentPackageCount) !== 0) || (formData.shipmentTotalWeight != null && formData.shipmentTotalWeight !== '' && Number(formData.shipmentTotalWeight) !== 0) || (formData.shipmentVolume != null && formData.shipmentVolume !== '' && Number(formData.shipmentVolume) !== 0) || (formData.shipmentDeclaredValue != null && formData.shipmentDeclaredValue !== '' && Number(formData.shipmentDeclaredValue) !== 0)) ? (
                  <table style={{ borderCollapse: 'collapse', fontSize: fs(12), fontFamily: '"Cormorant Garamond", serif', marginTop: '10px' }}>
                    <tbody>
                      {formData.shipmentPackageType && (
                        <tr>
                          <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{PREVIEW_FIELD_LABELS.packageType}</td>
                          <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{formData.shipmentPackageType}</td>
                        </tr>
                      )}
                      {formData.shipmentProductType && (
                        <tr>
                          <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{PREVIEW_FIELD_LABELS.productType}</td>
                          <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{formData.shipmentProductType}</td>
                        </tr>
                      )}
                      {(formData.shipmentPackageCount != null && formData.shipmentPackageCount !== '' && Number(formData.shipmentPackageCount) !== 0) && (
                        <tr>
                          <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{PREVIEW_FIELD_LABELS.packageCount}</td>
                          <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{formData.shipmentPackageCount}</td>
                        </tr>
                      )}
                      {(formData.shipmentTotalWeight != null && formData.shipmentTotalWeight !== '' && Number(formData.shipmentTotalWeight) !== 0) && (
                        <tr>
                          <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{PREVIEW_FIELD_LABELS.totalWeight}</td>
                          <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{Number(formData.shipmentTotalWeight).toLocaleString('ar-SA', { minimumFractionDigits: 1 })} kg</td>
                        </tr>
                      )}
                      {(formData.shipmentVolume != null && formData.shipmentVolume !== '' && Number(formData.shipmentVolume) !== 0) && (
                        <tr>
                          <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{PREVIEW_FIELD_LABELS.volume}</td>
                          <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{Number(formData.shipmentVolume).toLocaleString('ar-SA', { minimumFractionDigits: 2 })} m³</td>
                        </tr>
                      )}
                      {(formData.shipmentDeclaredValue != null && formData.shipmentDeclaredValue !== '' && Number(formData.shipmentDeclaredValue) !== 0) && (
                        <tr>
                          <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{PREVIEW_FIELD_LABELS.declaredValue}</td>
                          <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{formatBrandMoney(Number(formData.shipmentDeclaredValue))}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  ) : (
                    <div style={{ fontWeight: '600', fontSize: fs(16), color: '#3a3025', fontFamily: '"Playfair Display", serif' }}>---</div>
                  )}
                </div>
              </>
            ) : (
              <>
            {/* Client Info */}
            <div style={{ flex: '1', minWidth: '250px', maxWidth: '45%' }}>
              <div style={{
                fontSize: fs(12), 
                fontWeight: '700', 
                color: '#3a3025', 
                marginBottom: '10px',
                fontFamily: '"Playfair Display", serif',
                position: 'relative',
                paddingBottom: '8px',
                display: 'inline-block'
              }}>
                {billToTitle}
                <div style={{
                  position: 'absolute',
                  bottom: '0',
                  left: '0',
                  width: '40px',
                  height: '1px',
                  backgroundColor: '#c7b299'
                }}></div>
              </div>
                <>
                  <div style={{ fontWeight: '600', fontSize: fs(16), marginBottom: '10px', color: '#3a3025', fontFamily: '"Playfair Display", serif', }}>
                    {partnerName}
                  </div>
                  <table style={{ borderCollapse: 'collapse', fontSize: fs(12), fontFamily: '"Cormorant Garamond", serif' }}>
                    <tbody>
                      {partnerAddress && (
                        <tr>
                          <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{PREVIEW_FIELD_LABELS.address}</td>
                          <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{partnerAddress}</td>
                        </tr>
                      )}
                      {partnerNif && (
                        <tr>
                          <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{PREVIEW_FIELD_LABELS.vatNumber}</td>
                          <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{partnerNif}</td>
                        </tr>
                      )}
                      {partnerRc && (
                        <tr>
                          <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{PREVIEW_FIELD_LABELS.commercialRegister}</td>
                          <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{partnerRc}</td>
                        </tr>
                      )}
                      {partnerArt && (
                        <tr>
                          <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{PREVIEW_FIELD_LABELS.taxArticleNumber}</td>
                          <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{partnerArt}</td>
                        </tr>
                      )}
                      {partnerNis && (
                        <tr>
                          <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{PREVIEW_FIELD_LABELS.unifiedNationalNumber}</td>
                          <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{partnerNis}</td>
                        </tr>
                      )}
                      {partnerPhone && (
                        <tr>
                          <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{PREVIEW_FIELD_LABELS.phone}</td>
                          <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{partnerPhone}</td>
                        </tr>
                      )}
                      {partnerEmail && (
                        <tr>
                          <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{PREVIEW_FIELD_LABELS.email}</td>
                          <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{partnerEmail}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </>
            </div>

            {/* Document Details */}
            <div style={{ flex: '1', minWidth: '250px', maxWidth: '45%' }}>
              <div style={{
                fontSize: fs(12), 
                fontWeight: '700', 
                color: '#3a3025', 
                marginBottom: '10px',
                fontFamily: '"Playfair Display", serif',
                position: 'relative',
                paddingBottom: '8px',
                display: 'inline-block'
              }}>
                {documentDetailsLabel || 'تفاصيل المستند'}
                <div style={{
                  position: 'absolute',
                  bottom: '0',
                  left: '0',
                  width: '40px',
                  height: '1px',
                  backgroundColor: '#c7b299'
                }}></div>
              </div>
              <table style={{ borderCollapse: 'collapse', fontSize: fs(12), fontFamily: '"Cormorant Garamond", serif' }}>
                <tbody>
                  {useDetailRows && documentDetailRows.map((row) => (
                    <tr key={row.id}>
                      <td style={{ padding: '0 4px 0 0', color: '#000', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1.7', width: '1%' }}>{row.label}:</td>
                      <td style={{ padding: '0', color: '#7d735f', lineHeight: '1.7' }}>{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              </>
            )}
          </div>
        </div>
        )}

        {isDataListExport ? (
          <div style={useClosingLayoutFlex ? { flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0 } : undefined}>
            <div style={{ padding: `${sp(10)}px ${sp(32)}px`, width: '100%', boxSizing: 'border-box', direction: 'rtl', ...(useClosingLayoutFlex ? { flex: '1 1 auto', minHeight: 0, overflow: 'visible' } : {}) }}>
              {dataListExportContent}
            </div>
          </div>
        ) : (
          <>
        {isClosing && continuationHint && (
          <div style={{ padding: '12px 40px 0', fontSize: fs(11), color: '#555', fontStyle: 'italic' }}>
            {PREVIEW_LABELS.continuationLinesOnPreviousPage}
          </div>
        )}

        <div style={useClosingLayoutFlex ? { flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0 } : undefined}>
        {/* Items Table OR Payment Lines Table */}
        <div style={{ padding: `${sp(10)}px ${sp(40)}px 0px`, ...(useClosingLayoutFlex ? { flex: '0 0 auto' } : {}) }}>
          {!isClosing && (
          <>
          <div style={{
            fontSize: fs(13), 
            fontWeight: '700', 
            color: '#3a3025', 
            marginBottom: '10px',
            fontFamily: '"Playfair Display", serif',
            position: 'relative',
            paddingBottom: '8px',
            display: 'inline-block'
          }}>
            وصف العمليات
            <div style={{
              position: 'absolute',
              bottom: '0',
              left: '0',
              width: '60px',
              height: '1px',
              backgroundColor: '#c7b299'
            }}></div>
          </div>
          
          {(isPaymentVoucher || isPaymentReceipt) ? (
            /* Payment Lines Table for Bon de versement and Reçu de paiement */
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              marginBottom: '8px'
            }}>
              <thead>
                <tr>
                  <th style={{ 
padding: '6px 10px',
                    textAlign: 'left', 
                    fontWeight: '600', 
                    fontSize: fs(12),
                    color: '#3a3025',
                    borderBottom: '1px solid #e7e3da',
                    fontFamily: '"Playfair Display", serif',
                    backgroundColor: '#fbfaf8',
                    width: '70%'
                  }}>البيان</th>
                  <th style={{ 
padding: '6px 10px',
                    textAlign: 'right', 
                    fontWeight: '600', 
                    fontSize: fs(12),
                    color: '#3a3025',
                    borderBottom: '1px solid #e7e3da',
                    fontFamily: '"Playfair Display", serif',
                    backgroundColor: '#fbfaf8',
                    width: '30%'
                  }}>{PREVIEW_LABELS.amountHeader}</th>
                </tr>
              </thead>
              <tbody>
                {paymentLines.length > 0 ? paymentLines.map((line, index) => (
                  <tr key={index} style={{ backgroundColor: index % 2 === 1 ? '#fafafa' : 'white' }}>
                    <td style={{ padding: '5px 8px', fontSize: fs(12), color: '#111', borderBottom: '1px solid #f5f5f5', fontWeight: '500' }}>
                      {line.description || 'بند دفع'}
                    </td>
                    <td style={{ padding: '5px 8px', textAlign: 'right', fontSize: fs(12), color: '#111', borderBottom: '1px solid #f5f5f5', fontWeight: '500' }}>
                      {formatBrandMoney((line.amount || 0))}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="2" style={{ padding: '16px', textAlign: 'center', color: '#888', fontSize: fs(12) }}>
                      لا توجد بنود دفع
                    </td>
                  </tr>
                )}
                {/* Empty filler rows for payment-voucher/payment-receipt (max 5 rows total) */}
                {(isPaymentVoucher || isPaymentReceipt) && paymentLines.length > 0 && (() => {
                  const emptyRowsNeeded = Math.max(0, effectivePaymentRows - paymentLines.length);
                  return Array.from({ length: emptyRowsNeeded }).map((_, index) => (
                    <tr key={`empty-${index}`} style={{ backgroundColor: (paymentLines.length + index) % 2 === 1 ? '#fafafa' : 'white' }}>
                      <td style={{ padding: '5px 8px', fontSize: fs(12), borderBottom: '1px solid #f5f5f5', color: '#cccccc' }}>---</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', fontSize: fs(12), borderBottom: '1px solid #f5f5f5', color: '#cccccc' }}>---</td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          ) : (
            /* Regular Items Table for all other documents */
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              marginBottom: '8px'
            }}>
              <thead>
                <tr>
                  <th style={{ 
padding: '6px 10px',
                    textAlign: 'left', 
                    fontWeight: '600', 
                    fontSize: fs(12),
                    color: '#3a3025',
                    borderBottom: '1px solid #e7e3da',
                    fontFamily: '"Playfair Display", serif',
                    backgroundColor: '#fbfaf8'
                  }}>البيان</th>
                  <th style={{ 
padding: '6px 10px',
                    textAlign: 'center', 
                    fontWeight: '600', 
                    fontSize: fs(12),
                    color: '#3a3025',
                    borderBottom: '1px solid #e7e3da',
                    fontFamily: '"Playfair Display", serif',
                    backgroundColor: '#fbfaf8'
                  }}>الكمية</th>
                  {showPrices && (
                    <th style={{ 
padding: '6px 10px',
                    textAlign: 'right', 
                      fontWeight: '600', 
                      fontSize: fs(12),
                      color: '#3a3025',
                      borderBottom: '1px solid #e7e3da',
                      fontFamily: '"Playfair Display", serif',
                      backgroundColor: '#fbfaf8'
                    }}>سعر الوحدة</th>
                  )}
                  {showPrices && (
                    <th style={{ 
padding: '6px 10px',
                    textAlign: 'right', 
                      fontWeight: '600', 
                      fontSize: fs(12),
                      color: '#3a3025',
                      borderBottom: '1px solid #e7e3da',
                      fontFamily: '"Playfair Display", serif',
                      backgroundColor: '#fbfaf8'
                    }}>{PREVIEW_LABELS.amountHeader}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {items.length > 0 ? (
                  <>
                    {items.map((item, index) => (
                      <tr key={item.id || index} style={{ backgroundColor: index % 2 === 1 ? '#fafafa' : 'white' }}>
                        <td style={{ padding: '5px 8px', fontSize: fs(12), color: '#111', borderBottom: '1px solid #f5f5f5', fontWeight: '500' }}>
                          <div>{typeof item.product_name === 'object' ? (item.product_name?.label || item.product_name?.value || PREVIEW_LABELS.itemDefault) : (item.product_name || item.description || PREVIEW_LABELS.itemDefault)}</div>
                          {item.description && item.description !== item.product_name && (
                            <div style={{ fontSize: fs(11), color: '#777', marginTop: '2px' }}>{typeof item.description === 'object' ? (item.description?.label || item.description?.value || '') : item.description}</div>
                          )}
                        </td>
                        <td style={{ padding: '5px 8px', textAlign: 'center', fontSize: fs(12), color: '#111', borderBottom: '1px solid #f5f5f5', fontWeight: '500' }}>
                          {item.unit ? `${item.quantity || 0} ${typeof item.unit === 'object' ? (item.unit?.label || item.unit?.value || '') : item.unit}` : (item.quantity || 0)}
                        </td>
                        {showPrices && (
                          <td style={{ padding: '5px 8px', textAlign: 'right', fontSize: fs(12), color: '#111', borderBottom: '1px solid #f5f5f5', fontWeight: '500' }}>
                            {formatBrandMoney((item.unit_price || 0))}
                          </td>
                        )}
                        {showPrices && (
                          <td style={{ padding: '5px 8px', textAlign: 'right', fontSize: fs(12), color: '#111', borderBottom: '1px solid #f5f5f5', fontWeight: '500' }}>
                            {formatBrandMoney((item.total || 0))}
                          </td>
                        )}
                      </tr>
                    ))}
                    {Array.from({ length: Math.max(0, effectiveTableRows - items.length) }).map((_, index) => (
                      <tr key={`empty-${index}`} style={{ backgroundColor: (items.length + index) % 2 === 1 ? '#fafafa' : 'white' }}>
                        <td style={{ padding: '5px 8px', fontSize: fs(12), borderBottom: '1px solid #f5f5f5', color: '#cccccc' }}>---</td>
                        <td style={{ padding: '5px 8px', textAlign: 'center', fontSize: fs(12), borderBottom: '1px solid #f5f5f5', color: '#cccccc' }}>---</td>
                        {showPrices && <td style={{ padding: '5px 8px', textAlign: 'right', fontSize: fs(12), borderBottom: '1px solid #f5f5f5', color: '#cccccc' }}>---</td>}
                        {showPrices && <td style={{ padding: '5px 8px', textAlign: 'right', fontSize: fs(12), borderBottom: '1px solid #f5f5f5', color: '#cccccc' }}>---</td>}
                      </tr>
                    ))}
                  </>
                ) : (
                  <>
                    {Array.from({ length: effectiveTableRows }).map((_, index) => (
                      <tr key={`empty-${index}`} style={{ backgroundColor: index % 2 === 1 ? '#fafafa' : 'white' }}>
                        <td style={{ padding: '5px 8px', fontSize: fs(12), borderBottom: '1px solid #f5f5f5', color: '#cccccc' }}>---</td>
                        <td style={{ padding: '5px 8px', textAlign: 'center', fontSize: fs(12), borderBottom: '1px solid #f5f5f5', color: '#cccccc' }}>---</td>
                        {showPrices && <td style={{ padding: '5px 8px', textAlign: 'right', fontSize: fs(12), borderBottom: '1px solid #f5f5f5', color: '#cccccc' }}>---</td>}
                        {showPrices && <td style={{ padding: '5px 8px', textAlign: 'right', fontSize: fs(12), borderBottom: '1px solid #f5f5f5', color: '#cccccc' }}>---</td>}
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          )}
          </>
          )}

          {(((isPaymentVoucher || isPaymentReceipt) && !isClosing) || (!(isPaymentVoucher || isPaymentReceipt) && !isBody)) && (
          <>
          {isShippingSlip && finalAmount > 0 && (
            <div style={{ paddingTop: '12px', paddingBottom: '8px', fontSize: fs(11), fontStyle: 'italic', color: '#000000', textAlign: 'center', fontFamily: '"Cormorant Garamond", serif' }}>
              {buildAmountClosingLine(documentTypeFr, finalAmount)}
            </div>
          )}
          {/* Notes & Totals Row: Notes on left, Totals on right (BE: notes full width) */}
          <div style={{ display: 'grid', gridTemplateColumns: isShippingSlip ? '1fr' : '1fr 1fr', gap: '15px', marginTop: '10px', marginBottom: '0px' }}>
            {/* Left: Notes (hidden for payment-voucher/payment-receipt on body, shown after Arrêtée text) */}
            <div>
              {showSupplementary && !(isPaymentVoucher || isPaymentReceipt) && (
              <>
                <div style={{
                  fontSize: fs(12), 
                  fontWeight: '700', 
                  color: '#3a3025', 
                  marginBottom: '2px',
                  fontFamily: '"Playfair Display", serif',
                  position: 'relative',
                  paddingBottom: '8px',
                  display: 'inline-block'
                }}>
                  {String(supHeading).toUpperCase()}
                  <div style={{
                    position: 'absolute',
                    bottom: '0',
                    left: '0',
                    width: '40px',
                    height: '1px',
                    backgroundColor: '#c7b299'
                  }}></div>
                </div>
                <div style={{ fontSize: fs(12), color: '#7d735f', lineHeight: '1.8', fontFamily: '"Cormorant Garamond", serif', whiteSpace: 'pre-wrap', overflowY: 'visible' }}>
                  {supBody || '---'}
                </div>
              </>
              )}
            </div>

            {/* Right: Totals (hidden for bordereau d'expédition) */}
            {!isShippingSlip && (
            <div>
              {(isPaymentVoucher || isPaymentReceipt) && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}>
                  <div style={{ width: '300px' }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      padding: '15px 20px',
                      backgroundColor: '#3a3025',
                      color: '#fff',
                      fontWeight: '600',
                      fontSize: fs(16),
                      fontFamily: '"Playfair Display", serif'
                    }}>
                      <span>المبلغ الإجمالي:</span>
                      <span>{formatBrandMoney(paymentLines.reduce((sum, line) => sum + (parseFloat(line.amount) || 0), 0))}</span>
                    </div>
                  </div>
                </div>
              )}
              {showPrices && !isPaymentVoucher && !isPaymentReceipt && (
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
                <div style={{ width: '250px' }}>
                  {totalsRecapRows && totalsRecapRows.length > 0 ? (
                    totalsRecapRows.map((row, i) => (
                      <div
                        key={row.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: row.isFinal ? '8px 14px' : '2px 0',
                          marginTop: row.isFinal ? '2px' : 0,
                          backgroundColor: row.isFinal ? '#fbfaf8' : 'transparent',
                          border: row.isFinal ? '1px solid #e7e3da' : 'none',
                          borderBottom:
                            row.isFinal
                              ? '1px solid #e7e3da'
                              : totalsRecapRows[i + 1]?.isFinal
                                ? 'none'
                                : '1px solid #e7e3da',
                          fontFamily: row.isFinal ? '"Playfair Display", serif' : '"Cormorant Garamond", serif'
                        }}
                      >
                        <span style={{ color: row.isFinal ? '#3a3025' : '#7d735f', fontSize: row.isFinal ? fs(16) : '12px', fontWeight: row.isFinal ? '700' : '400' }}>{row.label}</span>
                        <span style={{ color: '#3a3025', fontWeight: '600', fontSize: row.isFinal ? fs(16) : '12px' }}>{row.text}</span>
                      </div>
                    ))
                  ) : !isReceptionNote ? (
                    <>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        padding: '5px 0', 
                        borderBottom: '1px solid #e7e3da'
                      }}>
                        <span style={{ color: '#7d735f', fontSize: fs(12), fontFamily: '"Cormorant Garamond", serif' }}>{PREVIEW_LABELS.subtotal}</span>
                        <span style={{ color: '#3a3025', fontWeight: '600', fontSize: fs(12), fontFamily: '"Cormorant Garamond", serif' }}>{formatBrandMoney(subtotal)}</span>
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        padding: '5px 0', 
                        borderBottom: '1px solid #e7e3da'
                      }}>
                        <span style={{ color: '#7d735f', fontSize: fs(12), fontFamily: '"Cormorant Garamond", serif' }}>{PREVIEW_LABELS.vat} ({getBrandVatRate()}%):</span>
                        <span style={{ color: '#3a3025', fontWeight: '600', fontSize: fs(12), fontFamily: '"Cormorant Garamond", serif' }}>{formatBrandMoney(taxAmount)}</span>
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        marginTop: '6px',
                        backgroundColor: '#fbfaf8',
                        border: '1px solid #e7e3da',
                        padding: '10px 14px',
                      }}>
                        <span style={{ color: '#3a3025', fontWeight: '700', fontSize: fs(14), fontFamily: '"Playfair Display", serif' }}>{PREVIEW_LABELS.totalIncVat}</span>
                        <span style={{ color: '#3a3025', fontWeight: '700', fontSize: fs(14), fontFamily: '"Playfair Display", serif' }}>{formatBrandMoney(total)}</span>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
              )}
            </div>
            )}
          </div>
          </>
          )}
        </div>

        {isReceptionNote && !isBody && finalAmount > 0 && (
          <div style={{ paddingTop: '12px', paddingLeft: '40px', paddingRight: '40px', paddingBottom: isClosing ? '28px' : '0px', fontSize: fs(11), fontStyle: 'italic', color: '#000000', textAlign: 'center', fontFamily: '"Cormorant Garamond", serif', ...(useClosingLayoutFlex ? { flex: '0 0 auto' } : {}) }}>
            {RECEPTION_INDICATIVE_HT_LABEL} <strong>{finalAmountInWords}</strong>
          </div>
        )}
        {(((isPaymentVoucher || isPaymentReceipt) && !isClosing) || (!(isPaymentVoucher || isPaymentReceipt) && !isBody)) && !['quote-request', 'proforma-request'].includes(documentType) && !isReceptionNote && !isShippingSlip && (
          <div style={{ paddingTop: '12px', paddingLeft: '40px', paddingRight: '40px', paddingBottom: isClosing ? '28px' : '0px', fontSize: fs(11), fontStyle: 'italic', color: '#000000', textAlign: 'center', fontFamily: '"Cormorant Garamond", serif', ...(useClosingLayoutFlex ? { flex: '0 0 auto' } : {}) }}>
            {buildAmountClosingLine(documentTypeFr, finalAmount)}
          </div>
        )}

        {/* Notes section for payment-voucher/payment-receipt (appears after Arrêtée text) */}
        {!isBody && (isPaymentVoucher || isPaymentReceipt) && showSupplementary && (
          <div style={{ paddingLeft: '40px', paddingRight: '40px', marginTop: compactNotesSignatureSpacing ? '10px' : '20px', marginBottom: compactNotesSignatureSpacing ? '4px' : '8px' }}>
            <div style={{
              fontSize: fs(14),
              fontWeight: '700',
              color: '#3a3025',
              marginBottom: '4px',
              fontFamily: '"Playfair Display", serif',
              position: 'relative',
              paddingBottom: '8px',
              display: 'inline-block'
            }}>
              {String(supHeading).toUpperCase()}
              <div style={{
                position: 'absolute',
                bottom: '0',
                left: '0',
                width: '40px',
                height: '1px',
                backgroundColor: '#c7b299'
              }}></div>
            </div>
            <div style={{ fontSize: fs(12), color: '#7d735f', lineHeight: '1.8', fontFamily: '"Cormorant Garamond", serif', whiteSpace: 'pre-wrap', overflowY: 'visible' }}>
              {supBody || '---'}
            </div>
          </div>
        )}

        {!isBody && (
        <div style={{
          ...(centerSignaturesInFooterGap
            ? { flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }
            : useClosingLayoutFlex
              ? { flex: '0 0 auto' }
              : {}),
        }}>
          <div style={{ paddingTop: (isPaymentVoucher || isPaymentReceipt) ? (compactNotesSignatureSpacing ? '10px' : '24px') : '15px', paddingRight: '40px', paddingBottom: '26px', paddingLeft: '40px', fontFamily: '"Cormorant Garamond", serif' }}>
          {/* One shared grid so column widths match the widest label per column — colons align across Bon / Établie / Reçu rows */}
          {(bon.enabled || etablieParInfo.enabled || recueParInfo.enabled) && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'max-content min-content minmax(0, 1.12fr) max-content min-content minmax(0, 0.95fr) max-content min-content 74px',
              columnGap: '8px',
              rowGap: compactNotesSignatureSpacing && (isPaymentVoucher || isPaymentReceipt) ? '12px' : '16px',
              fontSize: fs(12),
              alignItems: 'end',
              fontFamily: '"Cormorant Garamond", serif'
            }}>
              {bon.enabled && (
                <>
                  <span style={{ fontWeight: '600', color: '#3a3025', whiteSpace: 'nowrap', justifySelf: 'start', textTransform: 'uppercase', letterSpacing: '1px' }}>موافقة</span>
                  <span style={{ fontWeight: '600', color: '#3a3025', whiteSpace: 'nowrap' }}>:</span>
                  <span style={{
                    fontWeight: '400',
                    color: '#3a3025',
                    borderBottom: '1px dotted rgba(0, 0, 0, 0.25)',
                    paddingBottom: '2px',
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: '1.4',
                    minHeight: '18px'
                  }}>
                    {bon.name}
                  </span>
                  <span style={{ fontWeight: '600', color: '#3a3025', whiteSpace: 'nowrap', justifySelf: 'start', textTransform: 'uppercase', letterSpacing: '1px' }}>{PREVIEW_LABELS.signatureAndStamp}</span>
                  <span style={{ fontWeight: '600', color: '#3a3025', whiteSpace: 'nowrap' }}>:</span>
                  <span style={{
                    borderBottom: '1px dotted rgba(0, 0, 0, 0.25)',
                    paddingBottom: '2px',
                    lineHeight: '1.4',
                    minHeight: '18px'
                  }}>{' '}</span>
                  <span style={{ fontWeight: '600', color: '#3a3025', whiteSpace: 'nowrap', justifySelf: 'start', textTransform: 'uppercase', letterSpacing: '1px' }}>{PREVIEW_LABELS.signatureDate}</span>
                  <span style={{ fontWeight: '600', color: '#3a3025', whiteSpace: 'nowrap' }}>:</span>
                  <span style={{
                    fontWeight: '400',
                    color: '#3a3025',
                    borderBottom: '1px dotted rgba(0, 0, 0, 0.25)',
                    paddingBottom: '2px',
                    textAlign: 'center',
                    lineHeight: '1.4'
                  }}>
                    {bon.date || '---'}
                  </span>
                </>
              )}
              {etablieParInfo.enabled && (
                <>
                  <span style={{ fontWeight: '600', color: '#3a3025', whiteSpace: 'nowrap', justifySelf: 'start', textTransform: 'uppercase', letterSpacing: '1px' }}>أُعدّ بواسطة</span>
                  <span style={{ fontWeight: '600', color: '#3a3025', whiteSpace: 'nowrap' }}>:</span>
                  <span style={{
                    fontWeight: '400',
                    color: '#3a3025',
                    borderBottom: '1px dotted rgba(0, 0, 0, 0.25)',
                    paddingBottom: '2px',
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: '1.4',
                    minHeight: '18px'
                  }}>
                    {etablieParInfo.name}
                  </span>
                  <span style={{ fontWeight: '600', color: '#3a3025', whiteSpace: 'nowrap', justifySelf: 'start', textTransform: 'uppercase', letterSpacing: '1px' }}>{PREVIEW_LABELS.signatureAndStamp}</span>
                  <span style={{ fontWeight: '600', color: '#3a3025', whiteSpace: 'nowrap' }}>:</span>
                  <span style={{
                    borderBottom: '1px dotted rgba(0, 0, 0, 0.25)',
                    paddingBottom: '2px',
                    lineHeight: '1.4',
                    minHeight: '18px'
                  }}>{' '}</span>
                  <span style={{ fontWeight: '600', color: '#3a3025', whiteSpace: 'nowrap', justifySelf: 'start', textTransform: 'uppercase', letterSpacing: '1px' }}>{PREVIEW_LABELS.signatureDate}</span>
                  <span style={{ fontWeight: '600', color: '#3a3025', whiteSpace: 'nowrap' }}>:</span>
                  <span style={{
                    fontWeight: '400',
                    color: '#3a3025',
                    borderBottom: '1px dotted rgba(0, 0, 0, 0.25)',
                    paddingBottom: '2px',
                    textAlign: 'center',
                    lineHeight: '1.4'
                  }}>
                    {etablieParInfo.date || '---'}
                  </span>
                </>
              )}
              {recueParInfo.enabled && (
                <>
                  <span style={{ fontWeight: '600', color: '#3a3025', whiteSpace: 'nowrap', justifySelf: 'start', textTransform: 'uppercase', letterSpacing: '1px' }}>استلم</span>
                  <span style={{ fontWeight: '600', color: '#3a3025', whiteSpace: 'nowrap' }}>:</span>
                  <span style={{
                    fontWeight: '400',
                    color: '#3a3025',
                    borderBottom: '1px dotted rgba(0, 0, 0, 0.25)',
                    paddingBottom: '2px',
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: '1.4',
                    minHeight: '18px'
                  }}>
                    {recueParInfo.name}
                  </span>
                  <span style={{ fontWeight: '600', color: '#3a3025', whiteSpace: 'nowrap', justifySelf: 'start', textTransform: 'uppercase', letterSpacing: '1px' }}>{PREVIEW_LABELS.signatureAndStamp}</span>
                  <span style={{ fontWeight: '600', color: '#3a3025', whiteSpace: 'nowrap' }}>:</span>
                  <span style={{
                    borderBottom: '1px dotted rgba(0, 0, 0, 0.25)',
                    paddingBottom: '2px',
                    lineHeight: '1.4',
                    minHeight: '18px'
                  }}>{' '}</span>
                  <span style={{ fontWeight: '600', color: '#3a3025', whiteSpace: 'nowrap', justifySelf: 'start', textTransform: 'uppercase', letterSpacing: '1px' }}>{PREVIEW_LABELS.signatureDate}</span>
                  <span style={{ fontWeight: '600', color: '#3a3025', whiteSpace: 'nowrap' }}>:</span>
                  <span style={{
                    fontWeight: '400',
                    color: '#3a3025',
                    borderBottom: '1px dotted rgba(0, 0, 0, 0.25)',
                    paddingBottom: '2px',
                    textAlign: 'center',
                    lineHeight: '1.4'
                  }}>
                    {recueParInfo.date || '---'}
                  </span>
                </>
              )}
            </div>
          )}
        </div>
        </div>
        )}
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default HeritageInvoicePreview;




